import { request } from '@/axios';
import { rejectIfNotLogin } from '@/utils/requireLogin';
import { type ListOffsetParams, type ListResponse } from '@/types/list';
import { type ModelState } from '@/types/model';
import type { ArtworkTypeValue } from './types/asset-space';

// 作品列表项接口
export interface ArtworkItem {
  id: number;
  artworkName: string;
  preview: string | null;
  ctime: string;
  utime: string;
  modelList: string | null;
  avatar: string;
  nickname: string;
  originImgUrl: string;
}

// 获取作品列表请求参数
interface QueryArtworkListParams extends ListOffsetParams {
  artworkName?: string;
  orderCondition: number; // 1: 创建时间 2: 更新时间
  shareType?: number; // 0非共享 1共享
  /** 用户id */
  userId?: number;
  /** 团队id */
  teamId?: number;
}

// 获取作品列表
export function queryArtworkList(data: QueryArtworkListParams) {
  return request<ListResponse<ArtworkItem[]>>({
    api: '/api/artwork/list',
    method: 'post',
    data,
    notToast: false
  });
}

// 获取收藏列表
export interface FavoriteItem {
  id: number;
  image: string;
  width: number;
  height: number;
  param?: string | null;
  redrawWeight?: number | null;
  ctime?: string;
}
export function queryFavoriteImageList(data: ListOffsetParams) {
  return request<ListResponse<FavoriteItem[] | null>>({
    api: '/api/collect/list',
    method: 'post',
    data,
    notToast: false
  });
}

// 新版收藏列表 - 仅"我的资产/我的收藏"页使用
// 旧版 /api/collect/list 仍被 HistoryDrawer/ImageSelect/GalleryPanel 等复用,不要删
// 注:视频/3D相关字段(glb/mpFour/videoId/size等)后续重新开发时再加
export interface UserCollectListResp {
  id: number;
  image: string;
  width: number;
  height: number;
  ctime: string;
  param?: string | null;
  modelExampleId?: number; // 精选案例id,handleFavoriteDelete 用到
  /** 版本下实际图片列表（组图模块使用，用于精确计算素材数量） */
  images?: ArtworkImg[];
  /** 作品版本类型: img/video/group/psd */
  artworkVersionType?: string;
  /** 版本 ID */
  versionId?: number;
}
interface QueryFavoriteNewListParams {
  pageNo: number;
  pageSize: number;
  artworkType: number; // 0全部 1创作空间 3自由画布
  modules?: number[]; // module 多选筛选
}
export function queryFavoriteImageListNew(data: QueryFavoriteNewListParams) {
  return request<ListResponse<UserCollectListResp[] | null>>({
    api: '/api/collect/newList',
    method: 'post',
    data,
    notToast: false
  });
}

// 收藏、取消收藏
interface FavoriteImage {
  imgId: number;
  exampleId?: number;
  collect: 0 | 1; // 0: 取消收藏 1: 收藏
}
export function favoriteImage(data: FavoriteImage) {
  const loginRejected = rejectIfNotLogin();
  if (loginRejected) return loginRejected;

  return request<ListResponse<FavoriteItem[] | null>>({
    api: '/api/collect/collect',
    method: 'post',
    data,
    notToast: false
  });
}

interface QueryCollectModelData {
  pageNo: number;
  pageSize: number;
  offset?: number;
  loraType?: string; // Lora、Style
  ckptType?: string; // SDXL、Pony、Animagine
  level3Id?: number;
  notController?: boolean; // 过滤掉 Lora中的 Controller
  showType?: string; // 最新、最热排序
  notFlux?: boolean; // 过滤 HolopixV1 模型
}
export interface QueryModelListResult {
  records: ModelState[];
  total: number;
}
export function queryCollectModelList(data: QueryCollectModelData) {
  return request<ListResponse<ModelState[] | null>>({
    api: '/api/model/collectModelPublishList',
    method: 'post',
    data,
    notToast: false
  });
}

export function batchDeleteArtwork(data: {
  artworkIds: number[];
}) {
  return request({
    api: '/api/artwork/batchDelete',
    method: 'post',
    data,
    notToast: true
  });
}

// 模型共享
interface ShareModelRequest {
  id: number; // 模型id
}
export function shareModel(data: ShareModelRequest) {
  return request({
    api: '/api/model/shareModel',
    method: 'post',
    data,
    notToast: false
  });
}

// ============================================
// 版本列表接口
// ============================================

/**
 * 版本列表中的图片项
 */
export interface VersionImageItem {
  id: number;
  image: string;
  width: number;
  height: number;
  collect: 0 | 1;
  ctime: string;
  utime: string;
  [key: string]: any;
}

/**
 * 版本列表项（包含多张图片）
 */
export interface VersionRecord {
  id: number;
  artworkName: string;
  clientId: string;
  module: number;
  param: string;
  status: number;
  ctime: string;
  utime: string;
  images: VersionImageItem[];
  videoUrl?: string;
  [key: string]: any;
}

/**
 * 获取版本列表
 * 用于：图片、3D作品、视频 等 Tab 的数据获取
 * 通过 type 参数区分不同类型
 *
 * @param data.type - 类型：0-全部，1-图片，2-3D，3-视频
 */
interface QueryVersionListParams extends ListOffsetParams {
  type?: 0 | 1 | 2 | 3; // 0-全部, 1-图片, 2-3D, 3-视频
  artworkId?: number;
  module?: number;
  source?: number;
  userId?: number;
}

export function queryVersionList(data: QueryVersionListParams) {
  return request<ListResponse<VersionRecord[]>>({
    api: "/api/artwork/versionList",
    method: "post",
    data,
    notToast: false
  });
}

// ============================================
// 新版本列表接口 versionListNew
// ============================================

/**
 * 新接口请求参数
 * artworkType: 0全部, 1创作空间, 3自由画布, 5移动端作品
 */
export interface ArtworkVersionListNewReq {
  /** 作品类型: 0全部, 1创作空间, 3自由画布, 5移动端作品 */
  artworkType: ArtworkTypeValue;
  /** 模块列表（多选，不传查全部）— 我的空间子 Tab 过滤用 */
  moduleList?: number[];
  /** 团队id — 团队模式必传 */
  teamId?: number;
  /** 用户id */
  userId?: number;
  /** 作品id */
  artworkId?: number;
  /** 第几页 */
  pageNo?: number;
  /** 每页多少条 */
  pageSize?: number;
  /** 搜索关键词 — 创作空间用 */
  artworkName?: string;
  /** 排序条件 — 创作空间用 */
  orderCondition?: number;
}

/**
 * 新接口图片项
 */
export interface ArtworkImg {
  artworkId: number;
  collect: number;
  ctime: string;
  deleteStatus: number;
  glb: string;
  height: number;
  id: number;
  image: string;
  layerCount: number;
  pixeloeImg: string;
  redrawWeight: number;
  size: number;
  type: number;
  upload: number;
  userId: number;
  utime: string;
  versionId: number;
  width: number;
}

/**
 * 新接口视频项
 */
export interface ArtworkVideo {
  artworkId: number;
  collect: number;
  ctime: string;
  deleteStatus: number;
  id: number;
  mpFour: string;
  sequenceFrames: string;
  size: number;
  type: number;
  userId: number;
  utime: string;
  versionId: number;
  webp: string;
}

/**
 * 新接口响应项 — 版本级数据
 * 相比旧 VersionRecord 新增 artworkVersionType/nickname/avatar/shareType/finishTime/videos
 */
export interface ArtworkVersionListResp {
  /** 作品id */
  artworkId: number;
  /** 作品版本类型: img, video, group, psd */
  artworkVersionType: 'img' | 'video' | 'group' | 'psd';
  /** 头像 */
  avatar: string;
  /** 消息id */
  clientId: string;
  /** 创建时间 */
  ctime: string;
  /** 完成时间 */
  finishTime: string;
  /** 版本id */
  id: number;
  /** 图片信息 */
  images: ArtworkImg[];
  /** 模块 */
  module: number;
  /** 昵称 */
  nickname: string;
  /** 生图参数 */
  param: string;
  /** 生图id */
  promptId: string;
  /** 参考图id */
  referId: number;
  /** 团队版个人共享标识: 0非共享, 1共享 */
  shareType: 0 | 1;
  /** 来源: 1-直接生成, 2-再迭代 */
  source: 1 | 2;
  /** 状态 */
  status: number;
  /** 拆分结果客户端 manifest 稳定 CDN URL（module 87 拆分版本携带；打开拆分详情/复制到画布据此读图层） */
  resultManifestUrl?: string;
  /** 更新时间 */
  utime: string;
  /** 视频url */
  videoUrl: string;
  /** 视频信息 */
  videos: ArtworkVideo[];
}

/**
 * 新版本列表接口 — 我的资产用
 * 替代旧接口 queryVersionList → /api/artwork/versionList
 */
export function queryVersionListNew(data: ArtworkVersionListNewReq, notToast = false) {
  return request<ListResponse<ArtworkVersionListResp[]>>({
    api: '/api/artwork/versionListNew',
    method: 'post',
    data,
    notToast,
  });
}

// ============================================
// 视频列表接口（单独接口，待服务端提供）
// ============================================

/**
 * 视频列表项
 * 使用 versionList 接口返回的数据结构
 */
export interface VideoItem extends VersionRecord {
  // 从 VersionRecord 继承所有字段
}

/**
 * 获取视频列表
 * 使用 versionList 接口，type=3
 */
interface QueryVideoListParams extends ListOffsetParams {
  // 待服务端确认参数
}

export function queryVideoList(data: QueryVideoListParams) {
  return queryVersionList({ ...data, type: 3 }); // 3-视频
}

// ============================================
// 自由画布（画板）列表接口
// ============================================

/**
 * 画板列表请求参数
 * 注意：接口不需要传递空间类型参数，服务端根据登录用户自动区分个人/团队模式
 */
export interface CanvasBoardListReq {
  pageNo: number;
  pageSize: number;
  searchKey?: string;
  sort?: number; // 排序：1.创建时间，2.最后修改，3.字母排序
  type?: number; // 画板类型：0.智慧画板，1.无限画布
}

/**
 * 画板列表项
 */
export interface CanvasBoardItem {
  artworkId: number;
  boardName: string;
  coverImageUrl: string;
  ctime: string;
  utime: string;
  id: number;
  width: number;
  height: number;
}

/**
 * 获取画板列表
 * 用于：我的空间 - 自由画布 Tab
 */
export function queryCanvasBoardList(data: CanvasBoardListReq) {
  return request<ListResponse<CanvasBoardItem[]>>({
    api: "/api/board/list",
    method: "post",
    data,
    notToast: false
  });
}

/**
 * 批量收藏图片
 * @param data.imgIdList - 图片ID列表
 */
export function batchCollect(data: { imgIdList: number[] }) {
  const loginRejected = rejectIfNotLogin();
  if (loginRejected) return loginRejected;

  return request<string[]>({
    api: "/api/artwork/batchCollect",
    method: "post",
    data
  });
}

/**
 * 批量删除图片
 * @param data.imgIdList - 图片ID列表
 * @param data.versionIdList - 版本ID列表（可选）
 */
export function batchDeleteImages(data: { imgIdList: number[]; versionIdList?: number[] }) {
  return request<string[]>({
    api: "/api/artwork/batchDelVersion",
    method: "post",
    data
  });
}

// ============================================
// 图片详情接口（空间专用）
// ============================================

/**
 * 图片详情数据
 * 用于：作品详情抽屉右侧信息面板展示
 */
export interface ImageDetailData {
  /** 作品ID */
  artworkId: number;
  /** 作品名称 */
  artworkName: string;
  /** 收藏状态：0-未收藏，1-已收藏 */
  collect: 0 | 1;
  /** 图片ID */
  id: number;
  /** 图片地址 */
  image: string;
  /** 图片宽度 */
  width: number;
  /** 图片高度 */
  height: number;
  /** 生成参数对象 */
  param?: any;
  /** 版本ID */
  versionId: number;
  /** 类型 */
  type?: number;
  /** 图片创建时间 */
  imgCtime: string;
  /** 模块ID */
  module?: number;
  /** 3D模型地址 */
  glb?: string;
  /** 来源 */
  source?: number;
  /** 风格ID */
  styleId?: number | null;
  /** 重绘权重 */
  redrawWeight?: number | null;
  /** 精准像素图 - 仅像素图转换模块有该字段 */
  pixeloeImg?: string;
  multiImages?: string[];
  compareImage?: string;
  videoUrl?: string;
  /** PSD 图层数量（PSD 模块返回） */
  layerCount?: number;
}

/**
 * 获取图片详情
 * 用于：我的空间 - 作品详情抽屉
 * 注意：空间场景一定已登录，直接使用 /api 路径
 *
 * @param data.id - 图片ID
 */
export function queryImageDetail(data: { id: number }) {
  return request<ImageDetailData>({
    api: "/api/artwork/imgDetail",
    method: "post",
    data,
    notToast: false
  });
}

// ============================================
// 视频详情接口（空间专用）
// ============================================

/**
 * 视频详情数据
 * 用于：我的空间 - 视频详情弹窗
 * 与图片详情不同，source/module 在顶层而非 param 内
 */
export interface VideoDetailData {
  /** 作品ID */
  artworkId: number;
  /** 作品名称 */
  artworkName: string;
  /** 模块：25-视频生成，36-首尾帧生视频，37-参考生视频 */
  module: number;
  /** MP4格式视频URL */
  mpFour: string;
  /** 生成参数对象（type=1时才有值） */
  param?: any;
  /** 序列帧ZIP URL */
  sequenceFrames: string;
  /** 视频大小（MB） */
  size: number;
  /** 来源：1-直接生成，2-再迭代 */
  source: number;
  /** 风格id */
  styleId: number;
  /** 类型：1-标清480p，2-高清720p */
  type: number;
  /** 版本id */
  versionId: number;
  /** 视频创建时间 */
  videoCtime: string;
  /** WEBP格式视频URL */
  webp: string;
}

/**
 * 获取视频详情
 * 用于：我的空间 - 视频详情弹窗
 *
 * @param data.versionId - 版本ID
 */
export function queryVideoDetail(data: { versionId: number }) {
  return request<VideoDetailData>({
    api: "/api/artwork/videoDetail",
    method: "post",
    data,
    notToast: false
  });
}

/**
 * 通过 clientId 查询版本详情时返回的图片项
 */
export interface ArtworkVersionByClientIdImage extends VersionImageItem {
  artworkId: number;
  versionId: number;
  userId: number;
  type: number;
  glb: string;
  layerCount: number;
  obj: string;
  pixeloeImg?: string;
  size: number;
  upload: number;
  redrawWeight: number | null;
  deleteStatus: number;
}

/**
 * 通过 clientId 查询版本详情时返回的视频项
 */
export interface ArtworkVersionByClientIdVideo {
  artworkId: number;
  collect: 0 | 1;
  ctime: string;
  deleteStatus: number;
  id: number;
  mpFour: string;
  sequenceFrames: string;
  size: number;
  type: number;
  userId: number;
  utime: string;
  versionId: number;
  webp: string;
}

/**
 * 通过 clientId 查询的版本详情
 */
export interface ArtworkVersionByClientIdData {
  artworkId: number;
  artworkVersionType: 'img' | 'video' | 'group' | 'psd';
  avatar: string;
  clientId: string;
  ctime: string;
  finishTime: string;
  id: number;
  images: ArtworkVersionByClientIdImage[];
  module: number;
  nickname: string;
  param: string | Record<string, unknown> | null;
  promptId: string;
  referId: number;
  shareType: 0 | 1;
  source: 1 | 2;
  status: number;
  utime: string;
  videoUrl: string;
  videos: ArtworkVersionByClientIdVideo[];
}

/**
 * 根据自由画布节点的 clientId 获取完整版本详情
 */
export function queryVersionInfoByClientId(data: { clientId: string }) {
  return request<ArtworkVersionByClientIdData>({
    api: "/api/artwork/versionInfoByClientId",
    method: "post",
    data,
    notToast: true
  });
}
